import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, notifyAuthRequired, deleteOhlcvData, type OhlcvDeleteResult } from '../api/client';

interface SymbolData {
  symbol: string;
  interval: string;
  barCount: number;
  from: string;
  to: string;
}

interface DataOverview {
  totalBars: number;
  symbols: SymbolData[];
  supplementary: Record<string, number>;
}

async function fetchDataOverview(): Promise<{ data: DataOverview }> {
  const response = await fetch('/api/v1/admin/data/overview');
  if (!response.ok) {
    if (response.status === 401) notifyAuthRequired();
    const body = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, body.error ?? 'Failed to fetch data overview', body);
  }
  return response.json();
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function rowKey(s: { symbol: string; interval: string }): string {
  return `${s.symbol}|${s.interval}`;
}

export function DataOverviewPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['data-overview'],
    queryFn: fetchDataOverview,
  });

  const overview = data?.data;
  const rows = useMemo(() => overview?.symbols ?? [], [overview]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState<OhlcvDeleteResult | null>(null);

  // Drop selections for rows that no longer exist after a refetch
  useEffect(() => {
    const valid = new Set(rows.map(rowKey));
    setSelected((prev) => {
      const next = new Set([...prev].filter((k) => valid.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const selectedRows = rows.filter((s) => selected.has(rowKey(s)));
  const selectedBars = selectedRows.reduce((sum, s) => sum + s.barCount, 0);
  const allSelected = rows.length > 0 && selectedRows.length === rows.length;
  const someSelected = selectedRows.length > 0 && !allSelected;

  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const toggleRow = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(rows.map(rowKey)));
  };

  const deleteMutation = useMutation({
    mutationFn: () =>
      deleteOhlcvData(selectedRows.map(({ symbol, interval }) => ({ symbol, interval }))),
    onSuccess: (res) => {
      setLastResult(res.data);
      setSelected(new Set());
      setConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ['data-overview'] });
      queryClient.invalidateQueries({ queryKey: ['symbols'] });
    },
  });

  const openConfirm = () => {
    deleteMutation.reset();
    setLastResult(null);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (!deleteMutation.isPending) setConfirmOpen(false);
  };

  return (
    <div className="page">
      <h2>Data Overview</h2>
      <p className="page-subtitle">View imported market data and record counts.</p>

      {isLoading && <div className="loading-state">Loading data overview...</div>}
      {isError && (
        <div className="error-state">
          Failed to load: {(error as Error)?.message ?? 'Unknown error'}
        </div>
      )}

      {lastResult && (
        <div className="success-state">
          Deleted {lastResult.totalBarsDeleted.toLocaleString()} bars from{' '}
          {lastResult.deleted.length} symbol/interval pair{lastResult.deleted.length === 1 ? '' : 's'}.
          <button className="btn btn--small btn--secondary" onClick={() => setLastResult(null)}>
            Dismiss
          </button>
        </div>
      )}

      {overview && (
        <>
          <div className="dashboard-stats">
            <div className="stat-card">
              <span className="stat-card-value">{overview.totalBars.toLocaleString()}</span>
              <span className="stat-card-label">Total OHLCV Bars</span>
            </div>
            <div className="stat-card">
              <span className="stat-card-value">{overview.symbols.length}</span>
              <span className="stat-card-label">Symbol/Interval Pairs</span>
            </div>
          </div>

          <div className="card">
            <div className="card-header-row">
              <h3 className="card-title">OHLCV Data by Symbol</h3>
              {rows.length > 0 && (
                <div className="selection-toolbar">
                  {selectedRows.length > 0 && (
                    <>
                      <span className="selection-count">
                        {selectedRows.length} selected ({selectedBars.toLocaleString()} bars)
                      </span>
                      <button className="btn btn--small btn--secondary" onClick={() => setSelected(new Set())}>
                        Clear
                      </button>
                    </>
                  )}
                  <button
                    className="btn btn--small btn--danger"
                    onClick={openConfirm}
                    disabled={selectedRows.length === 0}
                  >
                    Delete selected
                  </button>
                </div>
              )}
            </div>
            {rows.length === 0 ? (
              <div className="empty-state">No market data imported yet. Go to Data Import to add data.</div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="checkbox-cell">
                        <input
                          ref={selectAllRef}
                          type="checkbox"
                          aria-label="Select all"
                          checked={allSelected}
                          onChange={toggleAll}
                        />
                      </th>
                      <th>Symbol</th>
                      <th>Interval</th>
                      <th>Bar Count</th>
                      <th>From</th>
                      <th>To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s) => {
                      const key = rowKey(s);
                      const isSelected = selected.has(key);
                      return (
                        <tr
                          key={key}
                          className={isSelected ? 'row--selected' : undefined}
                          onClick={() => toggleRow(key)}
                        >
                          <td className="checkbox-cell">
                            <input
                              type="checkbox"
                              aria-label={`Select ${s.symbol} ${s.interval}`}
                              checked={isSelected}
                              onChange={() => toggleRow(key)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td><strong>{s.symbol}</strong></td>
                          <td>{s.interval}</td>
                          <td>{s.barCount.toLocaleString()}</td>
                          <td>{formatDate(s.from)}</td>
                          <td>{formatDate(s.to)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <h3 className="card-title">Supplementary Data</h3>
            <div className="stats-grid">
              {Object.entries(overview.supplementary).map(([table, count]) => (
                <div className="stat-item" key={table}>
                  <span className="stat-label">{table.replace(/_/g, ' ')}</span>
                  <span className="stat-value">{count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {confirmOpen && (
        <ConfirmDeleteDialog
          rows={selectedRows}
          totalBars={selectedBars}
          isPending={deleteMutation.isPending}
          errorMessage={deleteMutation.isError ? (deleteMutation.error as Error)?.message ?? 'Unknown error' : null}
          onCancel={closeConfirm}
          onConfirm={() => deleteMutation.mutate()}
        />
      )}
    </div>
  );
}

interface ConfirmDeleteDialogProps {
  rows: SymbolData[];
  totalBars: number;
  isPending: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

function ConfirmDeleteDialog({ rows, totalBars, isPending, errorMessage, onCancel, onConfirm }: ConfirmDeleteDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div
        className="modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-delete-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="confirm-delete-title" className="modal-title">Delete OHLCV data?</h3>
        <p>
          This permanently deletes <strong>{totalBars.toLocaleString()} bars</strong> from{' '}
          <strong>{rows.length}</strong> symbol/interval pair{rows.length === 1 ? '' : 's'}. This cannot be undone.
        </p>
        <p className="modal-note">
          Active sessions trading these symbols will stop seeing this data immediately.
          You can re-import it later from Data Import.
        </p>

        <div className="table-container modal-list">
          <table className="data-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Interval</th>
                <th>Bars</th>
                <th>Range</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={rowKey(s)}>
                  <td><strong>{s.symbol}</strong></td>
                  <td>{s.interval}</td>
                  <td>{s.barCount.toLocaleString()}</td>
                  <td>{formatDate(s.from)} – {formatDate(s.to)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {errorMessage && <div className="error-state">Delete failed: {errorMessage}</div>}

        <div className="modal-actions">
          <button ref={cancelRef} className="btn btn--secondary" onClick={onCancel} disabled={isPending}>
            Cancel
          </button>
          <button className="btn btn--danger" onClick={onConfirm} disabled={isPending}>
            {isPending ? 'Deleting...' : `Delete ${rows.length} pair${rows.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
